// ===================================
// TRENCH QUEST - Main Game Engine
// Orchestrates all game systems
// ===================================

import type {
  GameSession,
  GameStatus,
  Difficulty,
  Portfolio,
  Player,
  PlayerTraits,
  GameGoal,
  GameWorld,
  GameTime,
} from '@/types'
import { initializeMarket, simulateMarket, type MarketEvent } from '../market/marketEngine'
import { initializeSimulatedMarket } from '../market/simulatedMarketEngine'
import type { SimulatedMarketState, SimulatedMarketConfig } from '@/types/simulatedMarket'
import { executeBuyOrder, executeSellOrder, type TradeResult } from '../trading/tradingEngine'
import { createGameTime, calculateTimePassed, getTimeScaleForDifficulty } from '../clock/gameClock'
import { generateGoal, DEGEN_QUOTES } from '../ai/prompts'
import { getAvailableTokens } from '../tokens/data'
import { gameSessionService } from '@/services/firebase/gameSession'
import { userProfileService } from '@/services/firebase/userProfile'

// Starting amounts based on difficulty (all start with $100k, goals vary)
const STARTING_AMOUNTS: Record<Difficulty, number> = {
  paper_hands: 100000, // $100k
  diamond_hands: 100000, // $100k
  gigabrain: 100000, // $100k
}

// Generate unique session ID
const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Create initial player
const createPlayer = (walletAddress: string): Player => {
  const now = new Date()
  return {
    walletAddress,
    portfolio: {
      holdings: [],
      cashBalance: 0,
      totalValue: 0,
    },
    traits: {
      degenScore: 50, // Start neutral
      trustScore: 50,
      wealthScore: 50,
      alphaScore: 50,
    },
    reputation: 50,
    createdAt: now,
    lastActiveAt: now,
  }
}

// Starting locations - varied for each new game
const STARTING_LOCATIONS = [
  'apartment',
  'coffee-shop',
  'trading-terminal',
  'degen-den',
  'hacker-house',
  'conference-hall',
  'rooftop-party',
  'nightclub-vip',
  'yacht-exterior',
  'penthouse-day',
]

// Create a SimulatedMarketState from existing session MarketState
const createSimulatedFromMarketState = (
  market: import('@/types').MarketState,
  session: GameSession
): SimulatedMarketState => {
  const prices: Record<string, import('@/types/simulatedMarket').SimulatedTokenPrice> = {}

  for (const [symbol, tokenPrice] of Object.entries(market.prices)) {
    prices[symbol] = {
      symbol,
      name: tokenPrice.symbol,
      baseRealPrice: tokenPrice.price,
      currentPrice: tokenPrice.price,
      priceHistory: [{
        timestamp: Date.now(),
        price: tokenPrice.price,
        gameHour: session.gameTime.totalGameHours,
      }],
      change24h: tokenPrice.change24h,
      change1h: 0,
      volatilityMultiplier: 1.0,
      driftBias: 0,
      pendingEffects: [],
      lastUpdateHour: session.gameTime.totalGameHours,
      isRugged: market.ruggedTokens.includes(symbol),
    }
  }

  return {
    prices,
    gameStartTime: session.startedAt.getTime(),
    currentGameHour: session.gameTime.totalGameHours,
    lastUpdateTime: Date.now(),
    lastUpdateGameHour: session.gameTime.totalGameHours,
    timeScale: session.gameTime.timeScale,
    condition: market.condition,
    fearGreedIndex: market.fearGreedIndex,
    globalVolatility: 1.0,
    ruggedTokens: market.ruggedTokens,
    recentEvents: [],
  }
}

// Convert SimulatedMarketState to MarketState for session storage
const convertSimulatedToMarketState = (simulated: SimulatedMarketState): import('@/types').MarketState => {
  const prices: Record<string, import('@/types').TokenPrice> = {}

  for (const symbol of Object.keys(simulated.prices)) {
    const token = simulated.prices[symbol]
    if (!token) continue
    prices[symbol] = {
      symbol,
      price: token.currentPrice,
      change24h: token.change24h,
      change7d: 0, // Not tracked in simulated market
      volume24h: 0, // Not tracked in simulated market
      marketCap: 0, // Not tracked in simulated market
      lastUpdate: Date.now(),
    }
  }

  return {
    prices,
    condition: simulated.condition,
    fearGreedIndex: simulated.fearGreedIndex,
    lastMajorEvent: null,
    ruggedTokens: simulated.ruggedTokens,
  }
}

// Create initial world state with random starting location
const createWorld = (): GameWorld => {
  const randomLocation = STARTING_LOCATIONS[Math.floor(Math.random() * STARTING_LOCATIONS.length)] || 'apartment'
  return {
    marketCondition: 'crab',
    currentLocation: randomLocation,
    activeNPCs: [],
    recentNews: [
      'Welcome to the trenches, anon.',
      'Markets looking uncertain. Perfect time to trade.',
    ],
  }
}

// Create new game session
export const createNewGame = async (
  walletAddress: string,
  difficulty: Difficulty
): Promise<{ session: GameSession; welcomeMessage: string; simulatedMarket: SimulatedMarketState | null }> => {
  const startingAmount = STARTING_AMOUNTS[difficulty]
  const timeScale = getTimeScaleForDifficulty(difficulty)

  // Generate goal
  const goalConfig = generateGoal(startingAmount, difficulty)
  const deadline = new Date()
  deadline.setDate(deadline.getDate() + goalConfig.daysLimit)

  const goal: GameGoal = {
    targetAmount: goalConfig.targetAmount,
    description: goalConfig.description,
    deadline,
    daysRemaining: goalConfig.daysLimit,
  }

  // Pick a random starting token for variety
  const STARTING_TOKENS: { symbol: string; name: string }[] = [
    { symbol: 'SOL', name: 'Solana' },
    { symbol: 'BONK', name: 'Bonk' },
    { symbol: 'WIF', name: 'dogwifhat' },
    { symbol: 'JUP', name: 'Jupiter' },
    { symbol: 'POPCAT', name: 'Popcat' },
    { symbol: 'FARTCOIN', name: 'Fartcoin' },
  ]
  const randomIndex = Math.floor(Math.random() * STARTING_TOKENS.length)
  const randomToken = STARTING_TOKENS[randomIndex] ?? STARTING_TOKENS[0]!

  // Initialize market FIRST with real Jupiter prices so we can use actual prices
  let market: import('@/types').MarketState
  let simulatedMarket: SimulatedMarketState | null = null

  try {
    const config: SimulatedMarketConfig = {
      difficulty,
      timeScale,
    }
    simulatedMarket = await initializeSimulatedMarket(config)
    market = convertSimulatedToMarketState(simulatedMarket)
  } catch (error) {
    console.warn('Failed to initialize simulated market, using fallback:', error)
    market = initializeMarket(difficulty)
  }

  // Get the ACTUAL market price for the starting token
  // This ensures portfolio value matches market reality
  const tokenPriceData = market.prices[randomToken.symbol]
  const actualPrice = tokenPriceData?.price ?? (
    // Fallback prices only if market doesn't have the token
    randomToken.symbol === 'SOL' ? 180 :
    randomToken.symbol === 'JUP' ? 1.2 :
    randomToken.symbol === 'BONK' ? 0.00003 :
    randomToken.symbol === 'WIF' ? 2.5 :
    randomToken.symbol === 'POPCAT' ? 1.5 :
    randomToken.symbol === 'FARTCOIN' ? 0.8 : 1.0
  )

  // Calculate token amount based on actual market price
  const tokenAmount = startingAmount / actualPrice

  // Create player with portfolio using actual market price
  const player = createPlayer(walletAddress)
  player.portfolio = {
    holdings: [
      {
        symbol: randomToken.symbol,
        name: randomToken.name,
        amount: tokenAmount,
        avgBuyPrice: actualPrice,
        currentPrice: actualPrice,
      },
    ],
    cashBalance: 0, // All in token, no cash
    totalValue: startingAmount,
  }

  // Create game time
  const gameTime = createGameTime({
    startDay: 1,
    startHour: 9, // Start at 9 AM
    timeScale,
  })

  // Create session
  const session: GameSession = {
    id: generateSessionId(),
    owner: walletAddress,
    status: 'active',
    difficulty,
    startedAt: new Date(),
    lastActiveAt: new Date(),
    currentDay: 1,
    startingToken: randomToken.symbol,
    startingAmount,
    goal,
    world: createWorld(),
    player,
    gameTime,
    market,
    tradeHistory: [],
    decisionCount: 0, // Track decisions for short-form gameplay
  }

  // Save to Firebase (non-blocking - game can start even if save fails)
  let savedSession = session
  try {
    savedSession = await gameSessionService.createSession(session)
  } catch (err) {
    console.error('Failed to save session to Firebase:', err)
    // Continue with local session - game still works
  }

  // Generate welcome message
  const quotes = DEGEN_QUOTES.login
  const holdingInfo = player.portfolio.holdings[0]
  const tokenDisplay = holdingInfo
    ? `$${startingAmount.toLocaleString()} worth of ${holdingInfo.symbol}`
    : `$${startingAmount.toLocaleString()} USDC`
  const welcomeMessage = `${quotes[Math.floor(Math.random() * quotes.length)]}

You're starting with ${tokenDisplay}.

Your mission: ${goal.description}

Target: $${goal.targetAmount.toLocaleString()}
Deadline: ${goalConfig.daysLimit} days

The trenches await. WAGMI... or NGMI. Your choice.`

  return { session: savedSession, welcomeMessage, simulatedMarket }
}

// Load existing game (handles offline time)
export const loadGame = async (
  walletAddress: string
): Promise<{
  session: GameSession
  timePassed: number
  marketEvents: MarketEvent[]
  timeSkipMessage: string
  simulatedMarket: SimulatedMarketState | null
} | null> => {
  const session = await gameSessionService.getActiveSession(walletAddress)

  if (!session) {
    return null
  }

  // Calculate time passed
  const { gameHoursPassed, newGameTime } = calculateTimePassed(session.gameTime)

  // If significant time passed, simulate market
  let marketEvents: MarketEvent[] = []
  let updatedMarket = session.market

  if (gameHoursPassed > 0.5) {
    const simulation = simulateMarket(session.market, gameHoursPassed, session.difficulty)
    updatedMarket = simulation.market
    marketEvents = simulation.events
  }

  // Update portfolio values with new prices
  const updatedHoldings = session.player.portfolio.holdings.map(holding => ({
    ...holding,
    currentPrice: updatedMarket.prices[holding.symbol]?.price || holding.currentPrice,
  }))

  const newTotalValue = updatedHoldings.reduce(
    (sum, h) => sum + h.amount * h.currentPrice,
    0
  )

  const updatedPortfolio: Portfolio = {
    ...session.player.portfolio,
    holdings: updatedHoldings,
    totalValue: newTotalValue,
  }

  // Check win/lose conditions
  let newStatus: GameStatus = session.status
  let statusMessage = ''

  if (newTotalValue >= session.goal.targetAmount) {
    newStatus = 'won'
    statusMessage = 'YOU MADE IT! Target reached!'
  } else if (newGameTime.gameDay > Math.ceil(
    (session.goal.deadline.getTime() - session.startedAt.getTime()) / (1000 * 60 * 60 * 24)
  )) {
    newStatus = 'lost'
    statusMessage = 'Time\'s up. NGMI this run.'
  } else if (newTotalValue < session.startingAmount * 0.01) {
    newStatus = 'lost'
    statusMessage = 'Portfolio rekt. Less than 1% remaining.'
  }

  // Update session
  const updatedSession: GameSession = {
    ...session,
    gameTime: newGameTime,
    market: updatedMarket,
    currentDay: newGameTime.gameDay,
    status: newStatus,
    player: {
      ...session.player,
      portfolio: updatedPortfolio,
      lastActiveAt: new Date(),
    },
  }

  // Save updates
  await gameSessionService.updateSession(session.id, {
    gameTime: newGameTime,
    market: updatedMarket,
    currentDay: newGameTime.gameDay,
    status: newStatus,
    player: updatedSession.player,
  })

  // If game ended, update user profile
  if (newStatus !== 'active') {
    await userProfileService.updateGameStats(
      walletAddress,
      newStatus === 'won' ? 'won' : 'lost',
      newTotalValue
    )
  }

  // Generate timeskip message
  let timeSkipMessage = ''
  if (gameHoursPassed > 1) {
    const hours = Math.floor(gameHoursPassed)
    timeSkipMessage = `You were away for ${hours} hour${hours !== 1 ? 's' : ''}.`

    if (marketEvents.length > 0) {
      timeSkipMessage += ` While you were gone:\n`
      for (const event of marketEvents.slice(0, 3)) {
        timeSkipMessage += `- ${event.headline}\n`
      }
    }

    const pnlWhileAway = newTotalValue - session.player.portfolio.totalValue
    const pnlPercent = (pnlWhileAway / session.player.portfolio.totalValue) * 100
    if (Math.abs(pnlPercent) > 1) {
      timeSkipMessage += `\nYour portfolio ${pnlWhileAway >= 0 ? 'gained' : 'lost'} ${Math.abs(pnlPercent).toFixed(1)}% while you were AFK.`
    }

    if (statusMessage) {
      timeSkipMessage += `\n\n${statusMessage}`
    }
  }

  // Create a simulated market state from the loaded session
  const simulatedMarket = createSimulatedFromMarketState(updatedMarket, updatedSession)

  return {
    session: updatedSession,
    timePassed: gameHoursPassed,
    marketEvents,
    timeSkipMessage,
    simulatedMarket,
  }
}

// Execute a buy trade
export const executeBuy = async (
  session: GameSession,
  toToken: string,
  usdcAmount: number
): Promise<TradeResult & { updatedSession: GameSession }> => {
  const result = executeBuyOrder(
    session.player.portfolio,
    session.market,
    session.player.traits,
    toToken,
    usdcAmount
  )

  if (result.success && result.trade) {
    // Update session
    const updatedSession: GameSession = {
      ...session,
      player: {
        ...session.player,
        portfolio: result.newPortfolio,
      },
      market: result.newMarket,
      tradeHistory: [...session.tradeHistory, result.trade],
    }

    // Save to Firebase
    await gameSessionService.updatePortfolio(session.id, result.newPortfolio)
    await gameSessionService.updateMarket(session.id, result.newMarket)
    await gameSessionService.addTrade(session.id, result.trade)

    // Update traits based on trade
    const traitUpdates = calculateTraitChanges('buy', toToken, usdcAmount, session)
    if (Object.keys(traitUpdates).length > 0) {
      updatedSession.player.traits = { ...updatedSession.player.traits, ...traitUpdates }
    }

    return { ...result, updatedSession }
  }

  return { ...result, updatedSession: session }
}

// Execute a sell trade
export const executeSell = async (
  session: GameSession,
  fromToken: string,
  amount: number
): Promise<TradeResult & { updatedSession: GameSession }> => {
  const result = executeSellOrder(
    session.player.portfolio,
    session.market,
    session.player.traits,
    fromToken,
    amount
  )

  if (result.success && result.trade) {
    // Update session
    const updatedSession: GameSession = {
      ...session,
      player: {
        ...session.player,
        portfolio: result.newPortfolio,
      },
      market: result.newMarket,
      tradeHistory: [...session.tradeHistory, result.trade],
    }

    // Save to Firebase
    await gameSessionService.updatePortfolio(session.id, result.newPortfolio)
    await gameSessionService.updateMarket(session.id, result.newMarket)
    await gameSessionService.addTrade(session.id, result.trade)

    return { ...result, updatedSession }
  }

  return { ...result, updatedSession: session }
}

// Calculate trait changes from actions
const calculateTraitChanges = (
  action: 'buy' | 'sell',
  token: string,
  amount: number,
  session: GameSession
): Partial<PlayerTraits> => {
  const changes: Partial<PlayerTraits> = {}
  const tokenData = getAvailableTokens(session.currentDay).find(t => t.symbol === token)

  if (!tokenData) return changes

  // Buying degen coins increases degen score
  if (action === 'buy' && ['degen', 'ultra_degen'].includes(tokenData.category)) {
    changes.degenScore = Math.min(100, (session.player.traits.degenScore || 50) + 2)
  }

  // Buying blue chips increases trust (you seem responsible)
  if (action === 'buy' && tokenData.category === 'blue_chip') {
    changes.trustScore = Math.min(100, (session.player.traits.trustScore || 50) + 1)
  }

  // Large position sizing affects degen score
  const positionSize = amount / session.player.portfolio.totalValue
  if (positionSize > 0.5) {
    changes.degenScore = Math.min(100, (changes.degenScore || session.player.traits.degenScore) + 5)
  }

  return changes
}

// Advance time manually (for actions that take time)
export const advanceTime = async (
  session: GameSession,
  hours: number
): Promise<{
  updatedSession: GameSession
  marketEvents: MarketEvent[]
}> => {
  // Simulate market for the time period
  const { market: updatedMarket, events } = simulateMarket(
    session.market,
    hours,
    session.difficulty
  )

  // Update game time
  const newGameTime: GameTime = {
    ...session.gameTime,
    totalGameHours: session.gameTime.totalGameHours + hours,
    gameHour: (session.gameTime.gameHour + hours) % 24,
    gameDay: session.gameTime.gameDay + Math.floor((session.gameTime.gameHour + hours) / 24),
    lastUpdateTimestamp: Date.now(),
  }

  // Update portfolio values
  const updatedHoldings = session.player.portfolio.holdings.map(holding => ({
    ...holding,
    currentPrice: updatedMarket.prices[holding.symbol]?.price || holding.currentPrice,
  }))

  const newTotalValue = updatedHoldings.reduce(
    (sum, h) => sum + h.amount * h.currentPrice,
    0
  )

  const updatedSession: GameSession = {
    ...session,
    gameTime: newGameTime,
    currentDay: newGameTime.gameDay,
    market: updatedMarket,
    player: {
      ...session.player,
      portfolio: {
        ...session.player.portfolio,
        holdings: updatedHoldings,
        totalValue: newTotalValue,
      },
    },
  }

  // Save to Firebase
  await gameSessionService.updateGameTime(session.id, newGameTime)
  await gameSessionService.updateMarket(session.id, updatedMarket)
  await gameSessionService.updatePortfolio(session.id, updatedSession.player.portfolio)

  return { updatedSession, marketEvents: events }
}

// Check and update game status
export const checkGameStatus = async (
  session: GameSession
): Promise<{ status: GameStatus; message: string }> => {
  const portfolioValue = session.player.portfolio.totalValue
  const deadline = session.goal.deadline

  // Win condition
  if (portfolioValue >= session.goal.targetAmount) {
    await gameSessionService.endGame(session.id, 'won', portfolioValue)
    await userProfileService.updateGameStats(session.owner, 'won', portfolioValue)
    return {
      status: 'won',
      message: `WAGMI! You hit $${portfolioValue.toLocaleString()}! Target was $${session.goal.targetAmount.toLocaleString()}. Screenshot this, king.`,
    }
  }

  // Time's up
  if (new Date() > deadline) {
    await gameSessionService.endGame(session.id, 'lost', portfolioValue)
    await userProfileService.updateGameStats(session.owner, 'lost', portfolioValue)
    return {
      status: 'lost',
      message: `Time's up! You ended with $${portfolioValue.toLocaleString()}. Needed $${session.goal.targetAmount.toLocaleString()}. NGMI this time.`,
    }
  }

  // Rekt (less than 1% remaining)
  if (portfolioValue < session.startingAmount * 0.01) {
    await gameSessionService.endGame(session.id, 'lost', portfolioValue)
    await userProfileService.updateGameStats(session.owner, 'lost', portfolioValue)
    return {
      status: 'lost',
      message: `REKT. Portfolio went to zero. ${DEGEN_QUOTES.rug[Math.floor(Math.random() * DEGEN_QUOTES.rug.length)]}`,
    }
  }

  return { status: 'active', message: '' }
}

// Get available tokens for current day
export const getAvailableTokensForSession = (session: GameSession) => {
  return getAvailableTokens(session.currentDay)
}

// Abandon game
export const abandonGame = async (session: GameSession): Promise<void> => {
  await gameSessionService.endGame(
    session.id,
    'abandoned',
    session.player.portfolio.totalValue
  )
}
